import { useContext, useState } from "react";
import api from "../../api";
import { useToast } from "../ToastContext";
import { LoadingContext } from "../LoadingContext";
import { AuthContext } from "../AuthContext";
import { set } from "lodash";

export interface CursoType {
    id: number
    name: string;
    idEmpresa?: number;
}

export interface CreateCursoType {
    name: string;
    empresaId: number;
}

export interface EditCursoType {
    id: number;
    name: string;
}

export default function useCurso() {
    const showToast = useToast();
    const { setLoading } = useContext(LoadingContext);
    const { handleLogout, objUserTk } = useContext(AuthContext);
    const [listCurso, setListCurso] = useState<CursoType[]>([]);
    const [listCursoByContrato, setListCursoByContrato] = useState<CursoType[]>([]);
    const [pageCount, setPageCount] = useState<number>(0);
    const [initialListCurso, setInitialListCurso] = useState<CursoType[]>([]);
    const [curso, setCurso] = useState<CursoType>({
        id: 0,
        name: "",
        idEmpresa: objUserTk.EmpresaId,
    });

    function createCurso(createCursoType: CreateCursoType): Promise<CreateCursoType | null> {
        setLoading(true);
        return api
            .post(`/Curso/create`, createCursoType)
            .then((response) => {
                const createdCurso: CreateCursoType = response.data;
                showToast("success", "Curso criado com sucesso.");
                setLoading(false);
                return createdCurso;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao criar curso. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao criar curso:", error);
                return null;
            });
    }

    function getCursoPaged(
        pageNumber: number,
        Name?: string,
        EmpresaId?: number,
        pageSize?: number
    ): Promise<CursoType[] | null> {
        setLoading(true);
        let url = `/Curso/getCursoPaged?pageNumber=${pageNumber}&pageSize=${pageSize}`;

        if (Name) {
            url += `&Name=${Name}`;
        }

        if (EmpresaId) {
            url += `&EmpresaId=${EmpresaId}`;
        }

        return api
            .get(url)
            .then((response) => {
                setListCurso(response.data.result);
                setInitialListCurso(response.data.result);
                setPageCount(response.data.pageCount);
                setLoading(false);
                return response.data.result;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao carregar cursos. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao carregar cursos:", error);
                return null;
            });
    }

    function updateCurso(updateCursoData: EditCursoType): Promise<EditCursoType | null> {
        setLoading(true);
        return api
            .put(`/Curso/${updateCursoData.id}`, updateCursoData)
            .then((response) => {
                showToast("success", "Curso atualizada com sucesso.");
                setLoading(false);
                return response.data;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao atualizar curso. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao atualizar curso:", error);
                return null;
            });
    }

    function deleteCurso(cursoId: number): Promise<CursoType | null> {
        setLoading(true);
        return api
            .delete(`/Curso/delete/${cursoId}`)
            .then(() => {
                setCurso({
                    id: 0,
                    name: "",
                });
                showToast("success", "Curso deletado com sucesso.");
                setLoading(false);
                return null;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao deletar curso. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao deletar curso:", error);
                return null;
            });
    }

    function handleGetCursoByEmpresaId(empresaId: number): Promise<CursoType[] | null> {
        setLoading(true);
        return api
            .get(`/Curso/getCursoByEmpresaId/${empresaId}`)
            .then((response) => {
                const curso: CursoType[] = response.data;
                setListCurso(curso);
                setLoading(false);
                return curso;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao buscar informações do curso. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao buscar informações do curso:", error);
                return null;
            });
    }
    
    function handleGetCursoById(cursoId: number): Promise<CursoType | null> {
        setLoading(true);
        return api
            .get(`/Curso/${cursoId}`)
            .then((response) => {
                const curso: CursoType = response.data;
                setCurso(curso);
                setLoading(false);
                return curso;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao buscar curso pelo ID. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao buscar curso pelo ID:", error);
                return null;
            });
    }  
    
    function handleGetCursoByContratoId(contratoId: number): Promise<CursoType[] | null> {
        setLoading(true);
        return api
            .get(`/Curso/getCursosByContratoId?contratoId=${contratoId}`)
            .then((response) => {
                const curso: CursoType[] = response.data;
                setListCursoByContrato(curso);
                setLoading(false);
                return curso;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao buscar informações do curso. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao buscar informações do curso:", error);
                return null;
            });
    }

    return {
        createCurso,
        getCursoPaged,
        updateCurso,
        deleteCurso,
        handleGetCursoByEmpresaId,
        handleGetCursoById,
        setListCurso,
        listCurso,
        setPageCount,
        pageCount,
        setInitialListCurso,
        initialListCurso,
        setCurso,
        curso,
        handleGetCursoByContratoId,
        listCursoByContrato,
        setListCursoByContrato,
    };
}
